import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import jsPDF from "jspdf";
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { theme } from '../../../theme';
import html2canvas from "html2canvas";
import { ApplicationFormModal } from "../../../components/Modals/ApplicationFormModal";
import {
  ApplicationFormData,
  useApplicationForm,
  formatDateToISO,
} from "../../../hooks/useApplicationForm";
import { useAuth } from "../../../contexts/AuthContext";
import { useSessionClass } from "contexts/SessionClassContext";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useStudents, ApplicationStudentDTO } from "../../../hooks/useStudents";
import { useTranslation } from "react-i18next";
import { useSchoolIdForAdmin } from "../../../hooks/useSchoolIdForAdmin";
import { useSchools, SchoolData } from "hooks/useSchools";
import { useClasses } from "../../../hooks/useClasses";
import { useSessions } from "../../../hooks/useSession";
import { useSubjects } from "../../../hooks/useSubjects";
import UploadBulkApplicationsModal from "components/Modals/UploadBulkApplicationsModal";
import SearchIcon from "@mui/icons-material/Search";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useTenantHeader } from "../../../hooks/useTenantHeader";
import ApplicationFormTemplate from "../../../components/templates/ApplicationFormTemplate";
import CloseIcon from "@mui/icons-material/Close";
import { useApiTracker } from "../../../hooks/useApiTracker";
import { useNetworkMonitor } from "../../../hooks/useNetworkMonitor";
import { useJwtExpiration } from "../../../hooks/useJwtExpiration";
import { useMedium } from "../../../components/layout/Layout";
import { useError } from "../../../contexts/ErrorContext";
import { getApplicationFormDraft, clearApplicationFormDraft } from "../../../utils/storage";

/* ------------------------ Small util: debounced value ----------------------- */
function useDebounce<T>(value: T, delay = 300) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

/**
 * ApplicationFormManagement (optimized)
 * - Debounced search
 * - React Query defaults tuned
 * - Optimistic cache updates for approve/reject/delete
 * - No per-item invalidations in bulk; at most one light refetch at end
 */
export default function ApplicationFormManagement() {
  const queryClient = useQueryClient();
  const { selectedSession, selectedClass } = useSessionClass();
  const { t } = useTranslation();
  const { user, isStudent, isSchoolAdmin, isTeacher } = useAuth();
  const isAppUser = user?.roles?.includes('APP_USER');
  const { tenantLoading } = useTenantHeader();
  // Disable students query for APP_USER - they don't need to fetch all students
  const {
    createApplicationStudentMutation,
    createBulkStudentMutation,
    useStudentByEmail,
    useStudentsBySessionIdAndClassIdAndStatus
  } = useStudents(
    tenantLoading,
    undefined,
    !isAppUser
  );
  // NOTE: Admission confirmation email is intentionally NOT sent on approval.
  const { selectedMedium } = useMedium();
  const { showError, showSuccess, showWarning } = useError();

  const apiTracker = useApiTracker();
  const networkMonitor = useNetworkMonitor();

  // Handle JWT expiration with user-friendly notifications
  useJwtExpiration();

  /* -------- React Query defaults: fewer surprises / fewer refetches -------- */
  useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: 1,
      },
    });
  }, [queryClient]);

  // Disable API call for school admin and teachers - they already have schoolId from user context
  const schoolIdForAdminQuery = useSchoolIdForAdmin(
    isSchoolAdmin() || isTeacher() ? undefined : user?.id
  );

  const { data: studentData, isLoading: studentLoading } = useStudentByEmail(
    isStudent() ? user?.email : undefined
  );

  // UI state
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentForm, setCurrentForm] = useState<any>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState<number | null>(null);
  const [formToReject, setFormToReject] = useState<number | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [formToApprove, setFormToApprove] = useState<number | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>("All");
  const [viewAdmissionModalOpen, setViewAdmissionModalOpen] = useState(false);
  const [viewAdmissionData, setViewAdmissionData] = useState<any>(null);
  const [openBulkModal, setOpenBulkModal] = useState(false);
  const [selectedApplications, setSelectedApplications] = useState<number[]>([]);
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);
  const [bulkRejectDialogOpen, setBulkRejectDialogOpen] = useState(false);
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);
  const [bulkRejectLoading, setBulkRejectLoading] = useState(false);
  const [bulkApproveProgress, setBulkApproveProgress] = useState<{
    current: number;
    total: number;
    currentStudentName: string;
    results: Array<{ success: boolean; id: number; status?: string }>;
  }>({ current: 0, total: 0, currentStudentName: '', results: [] });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Bulk download state
  const [bulkDownloadModalOpen, setBulkDownloadModalOpen] = useState(false);
  const [bulkDownloadSelectedClass, setBulkDownloadSelectedClass] = useState<number | ''>('');
  const [bulkDownloadSelectedSession, setBulkDownloadSelectedSession] = useState<number | ''>('');
  const [isGeneratingBulk, setIsGeneratingBulk] = useState(false);
  const [bulkDownloadError, setBulkDownloadError] = useState<string>('');
  const [bulkDownloadSuccess, setBulkDownloadSuccess] = useState<string>('');
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState(0);
  const [currentApplication, setCurrentApplication] = useState('');
  const [isBulkDownloadCancelled, setIsBulkDownloadCancelled] = useState(false);
  const [isPdfDownloaded, setIsPdfDownloaded] = useState(false); // Flag to prevent duplicate downloads
  const [bulkDownloadSelectedFormIds, setBulkDownloadSelectedFormIds] = useState<Set<number>>(new Set());
  const [restoreDraftDialogOpen, setRestoreDraftDialogOpen] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);

  // Reset status filter and page when session or class changes
  useEffect(() => {
    setSelectedStatus("All");
    setPage(0);
  }, [selectedSession, selectedClass]);

  // Warn on unload during bulk approve
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (bulkApproveLoading) {
        e.preventDefault();
        e.returnValue =
          t("bulk_approval_in_progress");
        return e.returnValue;
      }
    };
    if (bulkApproveLoading) window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [bulkApproveLoading]);

  // Axios interceptors for API tracking (once)
  useEffect(() => {
    const req = axios.interceptors.request.use(
      (config) => {
        const apiId = apiTracker.trackApiStart(
          config.url || "",
          config.method?.toUpperCase() || "GET"
        );
        (config as any).apiTrackerId = apiId;
        return config;
      },
      (error) => Promise.reject(error)
    );
    const res = axios.interceptors.response.use(
      (response) => {
        const apiId = (response.config as any).apiTrackerId;
        if (apiId) apiTracker.trackApiComplete(apiId, "completed");
        return response;
      },
      (error) => {
        const apiId = (error.config as any)?.apiTrackerId;
        if (apiId) apiTracker.trackApiComplete(apiId, "failed");
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(req);
      axios.interceptors.response.eject(res);
    };
  }, []); // eslint-disable-line

  // Compute schoolId - use user.schoolId directly for all roles
  const schoolId = useMemo(() => {
    if (isStudent() && studentData) return studentData.schoolId;
    else return user?.schoolId ?? undefined; // Use user.schoolId for both teachers and school admins, ensure no null
  }, [
    isStudent,
    studentData,
    user?.schoolId,
  ]);

  // Clear school-specific cache when schoolId changes to prevent cross-school data
  useEffect(() => {
    if (schoolId) {
      // Invalidate school-specific queries when schoolId changes
      queryClient.invalidateQueries({
        queryKey: ["applicationFormsBySchoolId", schoolId],
      });
    }
  }, [schoolId, queryClient]);

  // API hooks
  const {
    createApplicationFormMutation,
    updateApplicationFormMutation,
    deleteApplicationFormMutation,
    rejectApplicationFormMutation,
    uploadBulkApplicationsMutation,
    useApplicationFormsBySessionIdAndClassId,
    useApplicationFormsBySessionIdAndClassName,
  } = useApplicationForm(tenantLoading, schoolId);

  const classIdFilter =
    typeof selectedClass?.id === "number" && selectedClass.id > 0
      ? selectedClass.id
      : undefined;

  const classNameFilter =
    selectedClass?.section === "__GRADE_ALL__" || selectedClass?.name === "ALL"
      ? selectedClass?.name
      : undefined;

  // Fetch application forms by sessionId + (classId OR className for *-ALL / ALL)
  const applicationFormsBySessionIdAndClassIdQuery = useApplicationFormsBySessionIdAndClassId(
    selectedSession?.id,
    classIdFilter
  );

  const applicationFormsBySessionIdAndClassNameQuery = useApplicationFormsBySessionIdAndClassName(
    selectedSession?.id,
    classNameFilter
  );

  const applicationFormsQuery =
    classNameFilter ? applicationFormsBySessionIdAndClassNameQuery : applicationFormsBySessionIdAndClassIdQuery;

  // Fetch inactive students (status=false) for selected session/class and use them to
  // conditionally show "Delete" in APPROVED application rows.
  const inactiveStudentsBySessionClassQuery = useStudentsBySessionIdAndClassIdAndStatus(
    (isSchoolAdmin() || isTeacher()) ? selectedSession?.id : undefined,
    (isSchoolAdmin() || isTeacher()) ? classIdFilter : undefined
  );

  const inactiveStudentCodeSet = useMemo(() => {
    const list = inactiveStudentsBySessionClassQuery.data ?? [];
    return new Set(
      list
        .map((s: any) => (typeof s?.studentCode === "string" ? s.studentCode.trim() : ""))
        .filter(Boolean)
    );
  }, [inactiveStudentsBySessionClassQuery.data]);

  const inactiveStudentApplicationNoSet = useMemo(() => {
    const list = inactiveStudentsBySessionClassQuery.data ?? [];
    return new Set(
      list
        .map((s: any) => (typeof s?.applicationNo === "string" ? s.applicationNo.trim() : ""))
        .filter(Boolean)
    );
  }, [inactiveStudentsBySessionClassQuery.data]);

  const isApplicationStudentInactive = useCallback(
    (applicationRow: any) => {
      if (!applicationRow) return false;
      const studentCode =
        typeof applicationRow.studentCode === "string" ? applicationRow.studentCode.trim() : "";
      const applicationNo =
        typeof applicationRow.applicationNo === "string" ? applicationRow.applicationNo.trim() : "";

      if (studentCode && inactiveStudentCodeSet.has(studentCode)) return true;
      if (applicationNo && inactiveStudentApplicationNoSet.has(applicationNo)) return true;
      return false;
    },
    [inactiveStudentApplicationNoSet, inactiveStudentCodeSet]
  );

  // Classes and sessions hooks
  const { classesQuery } = useClasses(schoolId);
  const { sessionsQuery } = useSessions(schoolId);

  // Subjects hook to check if subjects exist for the selected class
  const { subjectsQuery: subjectsQueryForValidation } = useSubjects(tenantLoading, schoolId);

  // School query: sticky (not chatty)
  const schoolDataQuery = useQuery<SchoolData>({
    queryKey: ["schoolById", schoolId],
    queryFn: () =>
      axios
        .get(`${process.env.REACT_APP_API_URL}/schools/${schoolId}`)
        .then((res) => res.data),
    enabled: !!schoolId && !!user,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const schoolData = schoolDataQuery.data;
  const schoolLoading = schoolDataQuery.isLoading;

  // Check if subjects exist for the selected class
  const hasSubjectsForClass = useMemo(() => {
    if (!subjectsQueryForValidation.data || !selectedClass || !schoolId) return false;

    // Extract base class name (e.g., "10th" from "10th-A" or "LKG" from "LKG-A")
    const getBaseName = (name: string) => name.split("-")[0].trim().toLowerCase();
    
    const selectedClassBase = getBaseName(selectedClass.name);
    if (!selectedClassBase) return false;

    return subjectsQueryForValidation.data.some((subject: any) => {
      const subjectClassBase = subject.className ? getBaseName(subject.className) : "";
      return subject.schoolId === schoolId && subjectClassBase === selectedClassBase;
    });
  }, [subjectsQueryForValidation.data, selectedClass, schoolId]);

  // Optimistic cache helpers - Focus on school-specific cache
  const updateAppInCaches = useCallback(
    (id: number, updater: (f: any) => any) => {
      // Update global cache (for compatibility)
      queryClient.setQueryData<any[]>(["applicationForms"], (old) =>
        (old ?? []).map((f) => (f?.id === id ? updater(f) : f))
      );
      // Update school-specific cache (primary data source)
      if (schoolId) {
        queryClient.setQueryData<any[]>(
          ["applicationFormsBySchoolId", schoolId],
          (old) => (old ?? []).map((f) => (f?.id === id ? updater(f) : f))
        );
      }
    },
    [queryClient, schoolId]
  );

  const removeAppFromCaches = useCallback(
    (id: number) => {
      // Update global cache (for compatibility)
      queryClient.setQueryData<any[]>(["applicationForms"], (old) =>
        (old ?? []).filter((f) => f?.id !== id)
      );
      // Update school-specific cache (primary data source)
      if (schoolId) {
        queryClient.setQueryData<any[]>(
          ["applicationFormsBySchoolId", schoolId],
          (old) => (old ?? []).filter((f) => f?.id !== id)
        );
      }
    },
    [queryClient, schoolId]
  );

  // Handlers
  const handleSubmit = useCallback(
    async (formData: ApplicationFormData) => {
      if (!selectedSession) return;
      if (!selectedClass) return;
      if (!schoolId) return;

      const oldClassId = selectedClass.id;
      const resolvedClassId = (() => {
        const allClasses = classesQuery?.data || [];
        const getBaseName = (name: string) => (name || "").split("-")[0].trim().toLowerCase();
        const admissionBase = getBaseName(formData.admissionClass || selectedClass.name || "");
        const nextSection = (formData.section || selectedClass.section || "").toString().trim().toLowerCase();

        const matched = allClasses.find((cls: any) => {
          const clsBase = getBaseName(cls?.name || "");
          const clsSection = (cls?.section || "").toString().trim().toLowerCase();
          return clsBase === admissionBase && clsSection === nextSection;
        });

        return matched?.id ?? oldClassId;
      })();

      const submitData: ApplicationFormData = {
        ...formData,
        sessionId: selectedSession.id,
        classId: resolvedClassId,
        sessionName: selectedSession.name,
        schoolId,
      };

      try {
        if (isEdit && currentForm?.id) {
          await updateApplicationFormMutation.mutateAsync({
            id: currentForm.id,
            data: submitData,
          });

          // Comprehensive cache invalidation to ensure immediate image updates
          // 1. Invalidate specific application form cache first
          await queryClient.invalidateQueries({
            queryKey: ["applicationForm", currentForm.id],
          });

          // 2. Invalidate photo-related queries (only application photo, not student photo)
          await queryClient.invalidateQueries({
            queryKey: ["applicationPhotoInfo", currentForm.id],
          });
          await queryClient.invalidateQueries({
            queryKey: ["applicationPhoto"],
          });

          // 3. Force refetch all application forms queries
          await queryClient.refetchQueries({
            queryKey: ["applicationForms"],
            type: "active",
          });
          await queryClient.refetchQueries({
            queryKey: ["applicationFormsBySchoolId", schoolId],
            type: "active",
          });

          // 4. Invalidate any photo queries with predicate-based matching (only application photo)
          await queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey;
              return (
                (Array.isArray(queryKey) &&
                  queryKey.length >= 2 &&
                  queryKey[0] === "applicationPhotoInfo" &&
                  queryKey[1] === currentForm.id) ||
                (Array.isArray(queryKey) &&
                  queryKey.length >= 1 &&
                  queryKey[0] === "applicationPhoto") ||
                (Array.isArray(queryKey) &&
                  queryKey.length >= 1 &&
                  queryKey[0] === "applicationForms" &&
                  queryKey.includes(currentForm.id))
              );
            }
          });

          // 5. Remove any cached data for this specific application form
          queryClient.removeQueries({
            queryKey: ["applicationForm", currentForm.id],
          });
          queryClient.removeQueries({
            queryKey: ["applicationPhotoInfo", currentForm.id],
          });
          // Remove application photo blob URLs that might be cached
          queryClient.removeQueries({
            predicate: (query) => {
              const queryKey = query.queryKey;
              return Array.isArray(queryKey) && queryKey[0] === "applicationPhoto";
            }
          });

          // 6. Invalidate application forms by sessionId and classId query to refresh the list
          if (selectedSession?.id && selectedClass?.id) {
            queryClient.invalidateQueries({
              queryKey: ["applicationFormsBySessionIdAndClassId", selectedSession.id, selectedClass.id],
            });
            if (resolvedClassId !== selectedClass.id) {
              queryClient.invalidateQueries({
                queryKey: ["applicationFormsBySessionIdAndClassId", selectedSession.id, resolvedClassId],
              });
            }
          }

          // Show success message after update
          showSuccess(t("application_form_updated_successfully"));
        } else {
          await createApplicationFormMutation.mutateAsync(submitData);
          // Clear draft data on successful creation
          clearApplicationFormDraft();
          showSuccess(t("application_form_created_successfully"));
          // optional: background refetch of inactive queries
          queryClient.refetchQueries({
            queryKey: ["applicationForms"],
            type: "inactive",
          });
          queryClient.refetchQueries({
            queryKey: ["applicationFormsBySchoolId", schoolId],
            type: "inactive",
          });
        }
        setModalOpen(false);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || t("error_submitting_form");
        showError(errorMessage);
      }
    },
    [
      selectedSession,
      selectedClass,
      schoolId,
      isEdit,
      currentForm?.id,
      createApplicationFormMutation,
      updateApplicationFormMutation,
      t,
      updateAppInCaches,
      queryClient,
      showError,
      showSuccess,
    ]
  );

  const handleSelectApplication = useCallback((applicationId: number) => {
    setSelectedApplications((prev) => {
      if (prev.includes(applicationId)) return prev.filter((id) => id !== applicationId);
      if (prev.length >= 150) {
        return prev;
      }
      return [...prev, applicationId];
    });
  }, []);

  const handleOpenAddModal = useCallback(() => {
    setIsEdit(false);

    // Check if there's saved draft data
    const savedDraft = getApplicationFormDraft();
    if (savedDraft) {
      setDraftData(savedDraft);
      setRestoreDraftDialogOpen(true);
    } else {
      setCurrentForm(undefined);
      setModalOpen(true);
    }
  }, []);
  const handleOpenEditModal = useCallback((form: any) => {
    setIsEdit(true);
    setCurrentForm(form);
    setModalOpen(true);
  }, []);
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setCurrentForm(undefined);
    setIsEdit(false);
    // Note: Draft is saved in ApplicationFormModal when cancel is clicked or modal is closed
    // We don't clear it here - it will be cleared on successful creation or when user chooses to discard
  }, []);

  const handleRestoreDraft = useCallback(() => {
    if (draftData) {
      setCurrentForm(draftData);
      setRestoreDraftDialogOpen(false);
      setModalOpen(true);
    }
  }, [draftData]);

  const handleDiscardDraft = useCallback(() => {
    clearApplicationFormDraft();
    setDraftData(null);
    setRestoreDraftDialogOpen(false);
    setCurrentForm(undefined);
    setModalOpen(true);
  }, []);

  const formatGender = useCallback((gender: string | null | undefined): string => {
    if (!gender) return "";
    const g = gender.toLowerCase();
    if (g === "m") return t("male");
    if (g === "f") return t("female");
    return gender;
  }, []);


  const getClassNameFromSelected = (selectedClass: any, admissionClass: string): string => {
    if (selectedClass?.name) {
      const part = selectedClass.name.split("-")[0].trim();
      return part || selectedClass.name;
    }
    return admissionClass || "";
  };

  // Helper function to extract meaningful error message from API errors
  const extractErrorMessage = (error: any): string => {
    // Get raw error message from various possible locations
    let rawMessage = '';
    const responseData = error?.response?.data;

    if (responseData) {
      if (responseData.message) rawMessage = responseData.message;
      else if (responseData.error) {
        rawMessage = typeof responseData.error === 'string'
          ? responseData.error
          : responseData.error.message || JSON.stringify(responseData.error);
      }
      else if (responseData.errorMessage) rawMessage = responseData.errorMessage;
      else if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
        rawMessage = responseData.errors[0].message || responseData.errors[0];
      }
      else if (typeof responseData === 'string') rawMessage = responseData;
    }

    // Fallback to error message if no response data
    if (!rawMessage) {
      rawMessage = error?.message || '';
    }

    // Filter out generic axios messages
    if (rawMessage.includes('Request failed with status code')) {
      rawMessage = '';
    }

    // Parse and convert technical errors to user-friendly messages
    if (rawMessage) {
      // Handle duplicate key constraint errors
      if (rawMessage.includes('duplicate key value violates unique constraint')) {
        // Extract constraint name and value
        const constraintMatch = rawMessage.match(/constraint "([^"]+)"/);
        const valueMatch = rawMessage.match(/Key \(([^)]+)\)=\(([^)]+)\)/);

        if (constraintMatch && valueMatch) {
          const constraintName = constraintMatch[1];
          const fieldName = valueMatch[1];
          const fieldValue = valueMatch[2];

          // Map constraint names to user-friendly messages
          if (constraintName.includes('email') || fieldName.includes('email')) {
            return t("email_already_exists_for_another_user");
          } else if (constraintName.includes('username') || fieldName.includes('username')) {
            return t("username_already_exists_for_another_user");
          } else if (constraintName.includes('mobile') || constraintName.includes('phone') || fieldName.includes('mobile')) {
            return t("mobile_number_already_exists_for_another_user");
          } else if (constraintName.includes('aadhar') || fieldName.includes('aadhar')) {
            return t("aadhar_number_already_exists_for_another_user");
          } else if (constraintName.includes('roll') || fieldName.includes('roll')) {
            return t("roll_number_already_exists_for_another_student");
          } else if (constraintName.includes('enrolment') || fieldName.includes('enrolment')) {
            return t("enrolment_number_already_exists_for_another_student");
          } else {
            return t("duplicate_entry_error") + `: ${fieldName}`;
          }
        }
      }

      // Handle foreign key constraint errors
      if (rawMessage.includes('violates foreign key constraint') || rawMessage.includes('foreign key constraint')) {
        return t("referenced_record_not_found");
      }

      // Handle not null constraint errors
      if (rawMessage.includes('null value in column') || rawMessage.includes('NOT NULL constraint')) {
        const columnMatch = rawMessage.match(/column "([^"]+)"/);
        if (columnMatch) {
          return t("required_field_missing") + `: ${columnMatch[1]}`;
        }
        return t("required_field_missing");
      }

      // Handle subject not found errors
      if (rawMessage.includes('Subject with code') && rawMessage.includes('not found')) {
        const subjectCodeMatch = rawMessage.match(/Subject with code (\d+)/);
        if (subjectCodeMatch) {
          return t("subject_not_found_with_code") + ` ${subjectCodeMatch[1]}`;
        }
        return t("subject_not_found");
      }

      // Handle class not found errors
      if (rawMessage.includes('Class') && rawMessage.includes('not found')) {
        return t("class_not_found");
      }

      // Handle session not found errors
      if (rawMessage.includes('Session') && rawMessage.includes('not found')) {
        return t("session_not_found");
      }

      // Handle school not found errors
      if (rawMessage.includes('School') && rawMessage.includes('not found')) {
        return t("school_not_found");
      }

      // Clean up SQL errors - remove technical details
      if (rawMessage.includes('could not execute statement') || rawMessage.includes('SQL') || rawMessage.includes('insert into') || rawMessage.includes('update')) {
        // Try to extract meaningful part before SQL details
        const beforeSql = rawMessage.split('[insert')[0].split('[update')[0].split('SQL [')[0].trim();
        if (beforeSql && beforeSql.length > 10) {
          // Recursively parse the cleaned message
          return extractErrorMessage({ response: { data: { message: beforeSql } } });
        }
        // If we can't extract meaningful part, return generic message
        return t("database_error_please_check_data");
      }

      // Remove common technical prefixes
      rawMessage = rawMessage
        .replace(/^ERROR:\s*/i, '')
        .replace(/^Error:\s*/i, '')
        .replace(/Detail:\s*/i, '')
        .trim();

      // If message still contains too much technical detail, provide generic message
      if (rawMessage.includes('constraint') || rawMessage.includes('violates') || rawMessage.includes('SQL')) {
        return t("data_validation_error_please_check_input");
      }

      return rawMessage;
    }

    // If we only have status code, provide a generic message
    const statusCode = error?.response?.status;
    if (statusCode === 500) {
      return t("internal_server_error_please_try_again");
    } else if (statusCode === 404) {
      return t("resource_not_found");
    } else if (statusCode === 403) {
      return t("no_permission_to_perform_action");
    } else if (statusCode === 401) {
      return t("session_expired_please_login");
    } else if (statusCode === 400) {
      return t("invalid_request_data");
    }

    return t("unknown_error");
  };

  // Bulk download helper functions
  const waitNextFrame = () =>
    new Promise<void>((resolve) => {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    });

  const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

  const yieldToBrowser = (forceDelay = false) =>
    new Promise<void>((resolve) => {
      if (forceDelay) {
        setTimeout(resolve, 50); // Reduced from 120ms
      } else if ('requestIdleCallback' in window) {
        // @ts-ignore
        requestIdleCallback(() => resolve(), { timeout: 10 }); // Reduced timeout from 100ms
      } else {
        setTimeout(resolve, 10); // Reduced from 40ms for faster processing
      }
    });

  // Image loading helpers
  async function waitForImages(container: HTMLElement) {
    const imgs = Array.from(container.querySelectorAll('img'));
    if (imgs.length === 0) return;
    await Promise.all(
      imgs.map(img => {
        if (img.complete && (img.naturalWidth || 0) > 0) return;
        return new Promise<void>(resolve => {
          const t = setTimeout(resolve, 3000);
          img.onload = img.onerror = () => { clearTimeout(t); resolve(); };
        });
      })
    );
  }

  async function waitForAllImages(container: HTMLElement) {
    let attempts = 0;
    const maxAttempts = 10; // Reduced from 20 to 10 for faster processing

    while (attempts < maxAttempts) {
      const imgs = Array.from(container.querySelectorAll('img'));
      const allLoaded = imgs.every(img =>
        img.complete && (img.naturalWidth || 0) > 0
      );

      if (allLoaded && imgs.length > 0) {
        return;
      }

      // Reduced sleep time from 100ms to 50ms for faster checking
      await sleep(50);
      attempts++;
    }

    // Continue even if images not fully loaded for speed
  }

  async function waitForFonts() {
    try {
      // @ts-ignore
      if (document.fonts?.ready) await (document as any).fonts.ready;
    } catch { }
  }

  // Bulk download constants and renderer
  const A4_MM = { w: 210, h: 297 };
  const RENDER_SCALE = 1.0;
  const JPEG_QUALITY = 0.66;

  // QueryClient for bulk generation
  const bulkQueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 5 * 60 * 1000,
      },
    },
  });

  // Bulk download renderer
  function createBulkDownloadRenderer(Template: any) {
    let host: HTMLDivElement | null = null;
    let root: ReturnType<typeof createRoot> | null = null;

    const mount = async () => {
      if (host) return;
      host = document.createElement('div');
      Object.assign(host.style, {
        position: 'absolute',
        left: '-10000px',
        top: '0',
        width: '210mm',
        minHeight: '297mm',
        overflow: 'visible',
        backgroundColor: '#ffffff',
        opacity: '0',
        pointerEvents: 'none',
        display: 'block',
        boxSizing: 'border-box',
      } as CSSStyleDeclaration);

      host.style.setProperty('background-color', '#ffffff', 'important');
      host.style.setProperty('background', '#ffffff', 'important');
      host.style.setProperty('font-family', 'Arial, sans-serif', 'important');
      host.style.setProperty('line-height', 'normal', 'important');
      host.style.setProperty('margin', '0', 'important');
      host.style.setProperty('padding', '0', 'important');

      const style = document.createElement('style');
      style.setAttribute('data-pdf-style', 'true');
      style.textContent = `
        [data-pdf-root] {
          width: 210mm !important;
          min-height: 297mm !important;
          background-color: white !important;
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
          line-height: normal !important;
        }
        [data-pdf-root] * {
          box-sizing: border-box !important;
        }
      `;
      document.head.appendChild(style);

      document.body.appendChild(host);
      root = createRoot(host);
    };

    const ensureRendered = async () => {
      if (!host) return;

      void host.offsetHeight;

      // Reduced frame waits for faster rendering
      await waitNextFrame();

      const pdfRoot = host.querySelector<HTMLElement>('[data-pdf-root]');
      if (!pdfRoot) {
        await sleep(50); // Reduced from 100ms
        return;
      }

      pdfRoot.style.width = '210mm';
      pdfRoot.style.minHeight = '297mm';
      pdfRoot.style.backgroundColor = 'white';
      pdfRoot.style.margin = '0';
      pdfRoot.style.padding = '0';
      pdfRoot.style.boxSizing = 'border-box';

      // Reduced retry attempts for faster processing
      let tries = 0;
      while (tries++ < 5) { // Reduced from 15 to 5
        const rect = pdfRoot.getBoundingClientRect();
        if (rect.width > 200 && rect.height > 200) break;
        await waitNextFrame();
      }

      // Parallel image and font loading for speed
      await Promise.all([
        waitForAllImages(host),
        waitForFonts()
      ]);

      // Minimal delay - reduced from 200ms to 50ms
      await sleep(50);
    };

    const renderOnce = async (applicationData: any) => {
      if (!root || !host) throw new Error(t("renderer_not_mounted"));
      root.render(
        <QueryClientProvider client={bulkQueryClient}>
          <ThemeProvider theme={theme}>
            <Template
              data={applicationData}
              schoolData={schoolData}
              renderForPDF={true}
            />
          </ThemeProvider>
        </QueryClientProvider>
      );
      await ensureRendered();
    };

    const snapshot = async () => {
      if (!host) throw new Error(t("renderer_not_mounted"));
      const pdfRoot = (host.querySelector('[data-pdf-root]') as HTMLElement) ?? host;
      const canvas = await html2canvas(pdfRoot, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        background: '#ffffff',
        logging: false,
        scale: RENDER_SCALE,
        width: pdfRoot.offsetWidth || pdfRoot.clientWidth,
        height: pdfRoot.offsetHeight || pdfRoot.clientHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: pdfRoot.offsetWidth || pdfRoot.clientWidth,
        windowHeight: pdfRoot.offsetHeight || pdfRoot.clientHeight,
        removeContainer: false,
        foreignObjectRendering: false,
        imageTimeout: 0,
        onclone: (clonedDoc: Document) => {
          const clonedRoot = clonedDoc.querySelector('[data-pdf-root]');
          if (clonedRoot) {
            (clonedRoot as HTMLElement).style.width = '210mm';
            (clonedRoot as HTMLElement).style.minHeight = '297mm';
            (clonedRoot as HTMLElement).style.backgroundColor = 'white';
            (clonedRoot as HTMLElement).style.margin = '0';
            (clonedRoot as HTMLElement).style.padding = '0';
            (clonedRoot as HTMLElement).style.boxSizing = 'border-box';
          }
        },
      } as any);
      const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
      if (!dataUrl || dataUrl.length < 1000) {
        throw new Error('Snapshot produced empty image');
      }
      return dataUrl;
    };

    const unmount = () => {
      if (!host) return;
      try { root?.unmount(); } catch { }
      host.parentNode?.removeChild(host);

      const styleElement = document.querySelector('style[data-pdf-style]');
      if (styleElement) {
        styleElement.remove();
      }

      root = null;
      host = null;
    };

    return { mount, renderOnce, snapshot, unmount };
  }

  // Data derivations - Use selected session + class filter query data
  const filteredApplicationForms = useMemo(() => {
    // Use sessionId + (classId or className) query data instead of getAll
    const sourceData = applicationFormsQuery.data || [];

    // Debug logging for application statuses
    const deletedCount = sourceData.filter((f: any) => f.status === false).length;

    const filtered = sourceData
      .filter((form: any) => form.status !== false) // Hide deleted applications
      // Note: No need to filter by sessionId/classId as API already returns filtered data
      .filter((form: any) => {
        // Status filtering based on isRegistered string value
        if (selectedStatus === "All") {
          return true; // Show all non-deleted applications
        } else if (selectedStatus === "Pending") {
          return form.isRegistered?.toLowerCase() === 'pending';
        } else if (selectedStatus === "Approved") {
          return form.isRegistered?.toLowerCase() === 'approved';
        } else if (selectedStatus === "Rejected") {
          return form.isRegistered?.toLowerCase() === 'rejected';
        }
        return true;
      })
      .filter((form: any) => {
        // Filter by medium if a specific medium is selected (and not "ALL" or empty)
        if (selectedMedium && selectedMedium.trim() !== "" && selectedMedium !== "ALL") {
          // Handle both string and array formats for medium
          const formMedium = form.medium;
          if (!formMedium) {
            // If form has no medium, include it (don't exclude)
            return true;
          }
          if (typeof formMedium === 'string') {
            // If medium is a string, check if it contains the selected medium
            if (!formMedium.toLowerCase().includes(selectedMedium.toLowerCase())) {
              return false;
            }
          } else if (Array.isArray(formMedium)) {
            // If medium is an array, check if it includes the selected medium
            if (!formMedium.some((m: string) => m.toLowerCase() === selectedMedium.toLowerCase())) {
              return false;
            }
          } else {
            // Unknown format, include it
            return true;
          }
        }
        return true;
      })
      .filter((form: any) => {
        if (!debouncedSearch) return true;
        const q = debouncedSearch.toLowerCase();
        return (
          form.applicationNo?.toLowerCase().includes(q) ||
          form.studentName?.toLowerCase().includes(q) ||
          form.gender?.toLowerCase().includes(q) ||
          form.mobileNo?.toLowerCase().includes(q)
        );
      })
      .sort((a: any, b: any) => {
        // Sort by student name alphabetically (A-Z)
        const nameA = (a.studentName || '').toLowerCase();
        const nameB = (b.studentName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

    return filtered;
  }, [
    applicationFormsQuery.data,
    debouncedSearch,
    selectedStatus,
    selectedMedium, // Add selectedMedium to dependencies
  ]);

  // Calculate counts based on all data (not filtered by status) - exclude deleted applications
  const allApplicationForms = useMemo(() => {
    const sourceData = applicationFormsQuery.data || [];
    return sourceData
      .filter((form: any) => form.status !== false); // Exclude deleted applications
    // Note: No need to filter by sessionId/classId as API already returns filtered data
  }, [applicationFormsQuery.data]);

  const pendingApplications = useMemo(
    () => allApplicationForms.filter((f: any) => f.isRegistered?.toLowerCase() === 'pending'),
    [allApplicationForms]
  );

  const approvedApplications = useMemo(
    () => allApplicationForms.filter((f: any) => f.isRegistered?.toLowerCase() === 'approved'),
    [allApplicationForms]
  );

  const rejectedApplications = useMemo(
    () => allApplicationForms.filter((f: any) => f.isRegistered?.toLowerCase() === 'rejected'),
    [allApplicationForms]
  );

  const paginatedData = useMemo(
    () =>
      filteredApplicationForms.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredApplicationForms, page, rowsPerPage]
  );

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      if (isChecked) {
        // Allow selecting both pending and rejected applications
        const allSelectableIds = filteredApplicationForms
          .filter((form: any) =>
            form.id !== undefined &&
            (form.isRegistered?.toLowerCase() === 'pending' || form.isRegistered?.toLowerCase() === 'rejected')
          )
          .map((form: any) => form.id as number);
        if (allSelectableIds.length > 150) {
          const first150 = allSelectableIds.slice(0, 150);
          setSelectedApplications(first150);
        } else {
          setSelectedApplications(allSelectableIds);
        }
      } else {
        setSelectedApplications([]);
      }
    },
    [filteredApplicationForms]
  );

  // Bulk download handlers
  const bulkDownloadVisibleApplications = useMemo(() => {
    const allApplications = applicationFormsBySessionIdAndClassIdQuery.data || [];
    return allApplications
      .filter(
        (form: any) =>
          form.classId === bulkDownloadSelectedClass &&
          form.sessionId === bulkDownloadSelectedSession &&
          form.status !== false &&
          typeof form.id === "number"
      )
      .slice()
      .sort((a: any, b: any) =>
        String(a.studentName || "").localeCompare(String(b.studentName || ""))
      );
  }, [
    applicationFormsBySessionIdAndClassIdQuery.data,
    bulkDownloadSelectedClass,
    bulkDownloadSelectedSession,
  ]);

  useEffect(() => {
    const ids = bulkDownloadVisibleApplications
      .map((f: any) => f.id)
      .filter((id: unknown): id is number => typeof id === "number");
    setBulkDownloadSelectedFormIds(new Set(ids));
  }, [bulkDownloadSelectedSession, bulkDownloadSelectedClass, bulkDownloadVisibleApplications]);

  const handleOpenBulkDownloadModal = () => {
    setBulkDownloadModalOpen(true);
    setBulkDownloadError('');
    setBulkDownloadSuccess('');
  };

  const handleCloseBulkDownloadModal = () => {
    if (isGeneratingBulk) return;
    setBulkDownloadModalOpen(false);
    setBulkDownloadSelectedClass('');
    setBulkDownloadSelectedSession('');
    setBulkDownloadError(''); // Clear error when closing modal
    setBulkDownloadSuccess(''); // Clear success when closing modal
    setIsBulkDownloadCancelled(false); // Reset cancellation flag when closing modal
    setBulkDownloadProgress(0);
    setCurrentApplication('');
    setIsPdfDownloaded(false); // Reset download flag when closing modal
    pdfDownloadedRef.current = false; // Reset ref flag when closing modal
    setBulkDownloadSelectedFormIds(new Set());
  };

  const handleBulkDownloadSessionChange = (e: any) => {
    setBulkDownloadSelectedSession(e.target.value);
    setBulkDownloadSelectedClass('');
    setBulkDownloadError(''); // Clear error when session changes
    setBulkDownloadSuccess(''); // Clear success message when session changes
  };

  const handleBulkDownloadClassChange = (e: any) => {
    setBulkDownloadSelectedClass(e.target.value);
    setBulkDownloadError(''); // Clear error when class changes
    setBulkDownloadSuccess(''); // Clear success message when class changes
  };

  const bulkDownloadSelectAllChecked =
    bulkDownloadVisibleApplications.length > 0 &&
    bulkDownloadVisibleApplications.every((f: any) =>
      bulkDownloadSelectedFormIds.has(f.id)
    );

  const bulkDownloadSelectAllIndeterminate =
    bulkDownloadSelectedFormIds.size > 0 &&
    !bulkDownloadSelectAllChecked;

  const handleCancelBulkDownload = () => {
    setIsBulkDownloadCancelled(true);
    setIsGeneratingBulk(false);
    setBulkDownloadProgress(0);
    setCurrentApplication('');
    // Show cancellation message immediately when user cancels
    setBulkDownloadError(t("bulk_download_cancelled"));
  };

  // Ref to track if PDF has been saved (prevents duplicate downloads)
  const pdfDownloadedRef = useRef(false);

  // Bulk download function
  const handleBulkDownload = async () => {
    // Safety check: Only proceed if modal is open (user explicitly clicked download button)
    if (!bulkDownloadModalOpen) {
      return;
    }

    // Prevent duplicate downloads - if already generating or already downloaded, return early
    if (isGeneratingBulk || pdfDownloadedRef.current) {
      return;
    }

    setBulkDownloadError('');
    setBulkDownloadSuccess('');
    setBulkDownloadProgress(0);
    setCurrentApplication('');
    setIsBulkDownloadCancelled(false);
    setIsPdfDownloaded(false); // Reset download flag
    pdfDownloadedRef.current = false; // Reset ref flag

    if (!bulkDownloadSelectedClass || !bulkDownloadSelectedSession) {
      setBulkDownloadError(t("please_select_both_class_session"));
      return;
    }

    const applicationsToDownload = bulkDownloadVisibleApplications.filter((form: any) =>
      bulkDownloadSelectedFormIds.has(form.id)
    );

    if (applicationsToDownload.length === 0) {
      setBulkDownloadError(
        bulkDownloadVisibleApplications.length === 0
          ? t("no_applications_found")
          : (t("please_select_students_to_download") as string) ||
              "Please select at least one application to download."
      );
      return;
    }

    setIsGeneratingBulk(true);

    try {
      const selectedClass = classesQuery?.data?.find((c: any) => c.id === bulkDownloadSelectedClass);
      const className: string = selectedClass?.section ? `${selectedClass.name} ${selectedClass.section}` : (selectedClass?.name || t("unknown"));
      const sessionName: string = sessionsQuery?.data?.find((s: any) => s.id === bulkDownloadSelectedSession)?.name || t("unknown");
      const filename = `bulk-application-forms-${className}-${sessionName}-${applicationsToDownload.length}-applications.pdf`;

      const renderer = createBulkDownloadRenderer(ApplicationFormTemplate);
      await renderer.mount();

      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: true,
      });

      let pageIndex = 0;

      for (let idx = 0; idx < applicationsToDownload.length; idx++) {
        // Check cancellation flag - if cancelled, stop processing and show error
        if (isBulkDownloadCancelled) {
          setBulkDownloadError(t("bulk_download_cancelled"));
          setBulkDownloadSuccess(''); // Clear any success message
          break;
        }

        const application = applicationsToDownload[idx];
        const applicationName = application.studentName || t("unknown_student");
        setCurrentApplication(applicationName);

        // Update progress immediately for better UX
        const currentProgress = Math.round(((idx + 1) / applicationsToDownload.length) * 100);
        setBulkDownloadProgress((prevProgress) => Math.max(prevProgress, currentProgress));

        // Render and capture - minimal delays for speed
        await renderer.renderOnce(application);
        const imgData = await renderer.snapshot();

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, 0, A4_MM.w, A4_MM.h, undefined, 'FAST');
        pageIndex++;

        // Only yield to browser every few items to maintain responsiveness without slowing down
        if (idx % 5 === 0 || idx === applicationsToDownload.length - 1) {
          await yieldToBrowser();
        }
      }

      renderer.unmount();

      // Only save and show success if download was not cancelled and not already downloaded
      if (!isBulkDownloadCancelled && !pdfDownloadedRef.current) {
        // Ensure progress reaches 100% before saving PDF
        setBulkDownloadProgress((prevProgress) => Math.max(prevProgress, 100));

        // Set ref flag BEFORE saving to prevent duplicate downloads
        pdfDownloadedRef.current = true;
        setIsPdfDownloaded(true);

        // Save PDF immediately - no delay needed
        pdf.save(filename);

        // Clear any error messages before showing success
        setBulkDownloadError('');

        // Show success message with proper props
        const successMessage = t("successfully_generated_bulk_pdf")
          .replace("{{count}}", applicationsToDownload.length.toString())
          .replace("{{className}}", className)
          .replace("{{sessionName}}", sessionName);
        showSuccess(successMessage);
      }
    } catch (error: any) {
      // Clear any previous messages before showing error
      setBulkDownloadError('');
      setBulkDownloadSuccess('');
      const errorMessage = error?.message || t("unknown_error");
      setBulkDownloadError(`${t("failed_to_generate_bulk_pdf")}: ${errorMessage}`);
    } finally {
      setIsGeneratingBulk(false);
      setBulkDownloadProgress(0);
      setCurrentApplication('');
      // Reset cancellation flag after cleanup
      setIsBulkDownloadCancelled(false);
      // Note: Keep isPdfDownloaded flag set to prevent duplicate downloads until modal closes
    }
  };

  // Helper function to map application data to Student DTO
  const mapApplicationToStudentDTO = useCallback((applicationToApprove: any): ApplicationStudentDTO => {
    return {
      applicationNo: applicationToApprove.applicationNo,
      rollNumber: applicationToApprove.rollNo,
      enrolmentNo: applicationToApprove.enrolmentNo,
      studentCode: applicationToApprove.studentCode || '', // Include studentCode from application form
      studentName: applicationToApprove.studentName,
      fatherName: applicationToApprove.fatherName,
      motherName: applicationToApprove.motherName,
      dateOfBirth: formatDateToISO(applicationToApprove.dateOfBirth),
      gender: applicationToApprove.gender,
      medium: applicationToApprove.medium,
      caste: applicationToApprove.caste,
      subCaste: applicationToApprove.subCaste,
      casteCertificate: applicationToApprove.casteCertificate,
      religion: applicationToApprove.religion,
      sssmid: applicationToApprove.sssmid,
      scholarNo: applicationToApprove.scholarNo,
      penNo: applicationToApprove.penNo,
      apparId: applicationToApprove.apparId,
      aadharNo: applicationToApprove.aadharNo,
      houseNo: applicationToApprove.houseNo,
      street: applicationToApprove.street,
      landmark: applicationToApprove.landmark,
      area: applicationToApprove.area,
      city: applicationToApprove.city,
      postOffice: applicationToApprove.postOffice,
      district: applicationToApprove.district,
      state: applicationToApprove.state,
      pinCode: applicationToApprove.pinCode,
      mobileNo: applicationToApprove.mobileNo,
      bankName: applicationToApprove.bankName,
      ifcCode: applicationToApprove.ifcCode,
      accountNo: applicationToApprove.accountNo,
      annualIncome: applicationToApprove.annualIncome,
      section: applicationToApprove.section,
      subjects: applicationToApprove.subjects || [],
      gmail: applicationToApprove.gmail,
      admissionDate: formatDateToISO(applicationToApprove.admissionDate),
      admissionClass: applicationToApprove.admissionClass,
      admissionType: applicationToApprove.admissionType || '',
      stream: applicationToApprove.stream || '', // Stream field with default empty string
      schoolId: applicationToApprove.schoolId,
      schoolClassId: applicationToApprove.classId || selectedClass?.id || 1,
      sessionName: selectedSession?.name || "",
      sessionId: applicationToApprove.sessionId || selectedSession?.id || 1,
      className: getClassNameFromSelected(
        selectedClass,
        applicationToApprove.admissionClass
      ),
      studentPhoto: (() => {
        if (applicationToApprove.studentPhoto instanceof File) {
          return applicationToApprove.studentPhoto.name;
        }
        return applicationToApprove.studentPhoto || applicationToApprove.studentPhotoUrl || '';
      })(),
      studentSignature: (() => {
        if (applicationToApprove.studentSignature instanceof File) {
          return applicationToApprove.studentSignature.name;
        }
        return applicationToApprove.studentSignature || applicationToApprove.studentSignatureUrl || '';
      })(),
      cwsnCode: applicationToApprove.cwsnCode || '',
      sambalKramank: applicationToApprove.sambalKramank || '',
      status: true,
    };
  }, [selectedClass, selectedSession]);

  // Approve single (parallel create + update) with optimistic cache update
  const handleApproveApplication = useCallback(
    async (applicationId: number, suppressSuccessMessage: boolean = false) => {
      try {
        const applicationToApprove = filteredApplicationForms.find(
          (app) => app.id === applicationId
        );
        if (!applicationToApprove) {
          return;
        }

        // Check if the session is active
        const applicationSessionId = applicationToApprove.sessionId || selectedSession?.id;
        if (applicationSessionId) {
          const applicationSession = sessionsQuery?.data?.find(
            (s: any) => s.id === applicationSessionId
          );
          if (applicationSession && !applicationSession.isActive) {
            showError(t("cannot_approve_student_in_inactive_session") || "Cannot approve student in inactive session. Only rejection or deletion is allowed.");
            return;
          }
        }

        const studentData: ApplicationStudentDTO = mapApplicationToStudentDTO(applicationToApprove);

        const updatedApplicationData = {
          ...applicationToApprove,
          isRegistered: 'Approved',
        };

        // Do not call updateApplicationFormMutation
        await createApplicationStudentMutation.mutateAsync(studentData);

        // Optimistic cache update, no invalidations
        updateAppInCaches(applicationId, (f) => ({ ...f, isRegistered: 'Approved' }));

        if (!suppressSuccessMessage) {
          showSuccess(t("application_approved_successfully"));
        }

        // If this is a single approval (not bulk), do optional light refresh of inactive caches only
        if (selectedApplications.length === 0) {
          queryClient.refetchQueries({
            queryKey: ["applicationForms"],
            type: "inactive",
          });
          queryClient.refetchQueries({
            queryKey: ["applicationFormsBySchoolId", schoolId],
            type: "inactive",
          });
          if (selectedStatus !== "All") setPage(0);
        }

        // Invalidate sessionId/classId query when approving individually (not in bulk loop)
        const approvalSessionId = applicationToApprove.sessionId || selectedSession?.id;
        const approvalClassId = applicationToApprove.classId || selectedClass?.id;
        if (approvalSessionId && approvalClassId && !suppressSuccessMessage) {
          queryClient.invalidateQueries({
            queryKey: ["applicationFormsBySessionIdAndClassId", approvalSessionId, approvalClassId]
          });
        }
      } catch (error: any) {

        const statusCode = error?.response?.status || 0;
        const actualErrorMessage = extractErrorMessage(error);

        // Show meaningful error messages - prioritize actual error message over status codes
        let errorMsg = '';
        if (statusCode === 401) {
          // Authentication errors are special - show login message
          errorMsg = t("session_expired_please_login");
        } else if (statusCode === 403) {
          // Authorization errors
          errorMsg = t("no_permission_create_student");
        } else {
          // For all other errors, show the actual meaningful error message extracted from API
          errorMsg = actualErrorMessage;
        }

        showError(errorMsg);
        throw error;
      }
    },
    [
      filteredApplicationForms,
      selectedClass,
      selectedSession,
      sessionsQuery,
      createApplicationStudentMutation,
      updateApplicationFormMutation,
      t,
      selectedApplications.length,
      queryClient,
      schoolId,
      selectedStatus,
      updateAppInCaches,
      showError,
      showSuccess,
      showWarning,
      user,
    ]
  );

  // Helper function to check if application's session is inactive
  const isApplicationSessionInactive = useCallback((application: any): boolean => {
    if (!application) return false;
    const applicationSessionId = application.sessionId || selectedSession?.id;
    if (!applicationSessionId) return false;
    const applicationSession = sessionsQuery?.data?.find(
      (s: any) => s.id === applicationSessionId
    );
    return applicationSession ? !applicationSession.isActive : false;
  }, [sessionsQuery, selectedSession]);

  // Bulk approve (bulk API), no invalidations during loop; one refetch at end
  const handleBulkApprove = useCallback(
    async (applicationIds: number[]) => {
      try {
        // Filter out applications in inactive sessions
        const selectedApps = applicationIds
          .map(id => filteredApplicationForms.find(app => app.id === id))
          .filter((app): app is ApplicationFormData => !!app && !isApplicationSessionInactive(app));

        const skippedCount = applicationIds.length - selectedApps.length;
        if (skippedCount > 0) {
          showWarning(
            t("skipped_applications_in_inactive_session") ||
            `${skippedCount} application(s) in inactive session(s) were skipped. Only applications in active sessions can be approved.`
          );
        }

        if (selectedApps.length === 0) {
          showError(t("no_applications_can_be_approved_inactive_session") || "No applications can be approved. All selected applications are in inactive sessions.");
          return;
        }

        setBulkApproveLoading(true);
        setBulkApproveProgress({
          current: 0,
          total: selectedApps.length,
          currentStudentName: t("preparing_bulk_data") || "Preparing bulk data...",
          results: [],
        });

        // 1. Prepare bulk student data
        const bulkStudentData: ApplicationStudentDTO[] = selectedApps.map(app => mapApplicationToStudentDTO(app));

        // 2. Call Bulk Student Creation API
        await createBulkStudentMutation.mutateAsync(bulkStudentData);

        // 3. Update all application forms to 'Approved' & Send emails
        // Status is updated on backend via bulk-approve, we just update local cache optimistically
        setBulkApproveProgress(prev => ({ ...prev, currentStudentName: t("finalizing_approval") || "Finalizing approval..." }));

        selectedApps.forEach(app => {
          // Optimistic cache update
          if (app.id) {
            updateAppInCaches(app.id, (f) => ({ ...f, isRegistered: 'Approved' }));
          }
        });

        showSuccess(t("bulk_approval_successful", { count: selectedApps.length }));
        setSelectedApplications([]);
        setBulkApproveDialogOpen(false);
        setBulkApproveProgress({ current: 0, total: 0, currentStudentName: '', results: [] });

        // Final background refresh
        queryClient.invalidateQueries({ queryKey: ["students"] });
        if (selectedSession?.id && selectedClass?.id) {
          queryClient.invalidateQueries({
            queryKey: ["applicationFormsBySessionIdAndClassId", selectedSession.id, selectedClass.id]
          });
        }
        if (selectedStatus !== "All") setPage(0);

      } catch (error: any) {
        setBulkApproveLoading(false);
        const actualErrorMessage = extractErrorMessage(error);
        showError(actualErrorMessage || t("bulk_approval_failed"));
      } finally {
        setBulkApproveLoading(false);
      }
    },
    [
      filteredApplicationForms,
      isApplicationSessionInactive,
      mapApplicationToStudentDTO,
      createBulkStudentMutation,
      updateApplicationFormMutation,
      updateAppInCaches,
      queryClient,
      selectedSession,
      selectedClass,
      selectedStatus,
      showError,
      showSuccess,
      showWarning,
      t
    ]
  );


  // Reject single (optimistic update to set isRegistered: 'Rejected')
  const handleRejectApplication = useCallback(
    async (applicationId: number) => {
      try {
        const applicationToReject = filteredApplicationForms.find(
          (app) => app.id === applicationId
        );
        await rejectApplicationFormMutation.mutateAsync(applicationId);
        // Optimistically update cache to set isRegistered: 'Rejected'
        updateAppInCaches(applicationId, (f) => ({ ...f, isRegistered: 'Rejected' }));

        // Invalidate sessionId/classId query when rejecting to show status immediately
        const rejectSessionId = applicationToReject?.sessionId || selectedSession?.id;
        const rejectClassId = applicationToReject?.classId || selectedClass?.id;
        if (rejectSessionId && rejectClassId) {
          queryClient.invalidateQueries({
            queryKey: ["applicationFormsBySessionIdAndClassId", rejectSessionId, rejectClassId]
          });
        }

        showSuccess(t("application_rejected_successfully"));
        setRejectDialogOpen(false);
        setFormToReject(null);
        if (selectedStatus !== "All") setPage(0);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || t("error_rejecting_application");
        showError(errorMessage);
      }
    },
    [rejectApplicationFormMutation, updateAppInCaches, selectedStatus, showError, showSuccess, t, filteredApplicationForms, selectedSession, selectedClass, queryClient]
  );

  // Approve single with confirmation dialog
  const handleApproveApplicationWithConfirmation = useCallback(
    async (applicationId: number) => {
      try {
        const applicationToApprove = filteredApplicationForms.find(
          (app) => app.id === applicationId
        );
        if (applicationToApprove && isApplicationSessionInactive(applicationToApprove)) {
          showError(t("cannot_approve_student_in_inactive_session") || "Cannot approve student in inactive session. Only rejection or deletion is allowed.");
          setApproveDialogOpen(false);
          setFormToApprove(null);
          return;
        }
        await handleApproveApplication(applicationId);
        setApproveDialogOpen(false);
        setFormToApprove(null);
      } catch (error) {
      }
    },
    [handleApproveApplication, filteredApplicationForms, isApplicationSessionInactive, showError, t]
  );

  // Loading gate
  const shouldWaitForApplicationForms = !!selectedSession?.id && (!!classIdFilter || !!classNameFilter);

  // For school admin and teachers, don't wait for schoolIdForAdminQuery or schoolLoading since they already have schoolId
  const isWaitingForSchoolData = !isSchoolAdmin() && !isTeacher() && (schoolIdForAdminQuery.isLoading || schoolLoading);

  if (
    tenantLoading ||
    (isStudent() && studentLoading) ||
    isWaitingForSchoolData ||
    !schoolId ||
    (shouldWaitForApplicationForms && applicationFormsQuery.isLoading)
  ) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // If school admin but no schoolId, show error instead of infinite loading
  if (isSchoolAdmin() && !schoolId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="warning" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            {t("school_configuration_issue")}
          </Typography>
          <Typography variant="body2">
            {t("account_not_properly_configured")}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        {(!selectedSession || !selectedSession.isActive || !selectedClass || !hasSubjectsForClass) && (
          <Alert severity="warning" sx={{ mb: 2, backgroundColor: "#fff3cd", color: "#856404" }}>
            {t("please_create_class_active_session_and_subjects_before_creating_application_form")}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("application_form_management")}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {(isSchoolAdmin() || isTeacher()) && (
              <>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
                  onClick={handleOpenAddModal}
                  disabled={!selectedSession?.isActive || !selectedClass || !hasSubjectsForClass}
                >
                  {t("add_application_form")}
                </Button>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, ml: 2 }}
                  onClick={() => setOpenBulkModal(true)}
                  disabled={!selectedSession?.isActive || !hasSubjectsForClass}
                >
                  {t("upload_bulk_applications")}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#3092e3",
                    "&:hover": { backgroundColor: "primary.main" },
                    ml: 2,
                    minWidth: 140,
                  }}
                  onClick={handleOpenBulkDownloadModal}
                  disabled={isGeneratingBulk}
                >
                  {t("bulk_download")}
                </Button>

                {selectedApplications.length > 0 && (
                  <>
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, ml: 2 }}
                      onClick={() => setBulkApproveDialogOpen(true)}
                      disabled={
                        selectedApplications.some(
                          (id) => {
                            const app = filteredApplicationForms.find((f) => f.id === id);
                            return isApplicationSessionInactive(app);
                          }
                        )
                      }
                    >
                      {t("approve_selected")} ({selectedApplications.length})
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, ml: 2 }}
                      onClick={() => setBulkRejectDialogOpen(true)}
                    >
                      {t("reject_selected")} ({selectedApplications.length})
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Status + Search row */}
        <Box
          sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          {/* Selection warnings */}
          {selectedApplications.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: -40,
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {selectedApplications.length >= 150 ? (
                <Alert severity="warning" sx={{ maxWidth: "fit-content" }}>
                  {t("maximum_selection_limit_reached")}
                </Alert>
              ) : selectedApplications.length >= 200 ? (
                <Alert severity="info" sx={{ maxWidth: "fit-content" }}>
                  {t("warning_selection_limit", { count: 150 - selectedApplications.length })}
                </Alert>
              ) : null}
            </Box>
          )}

          {/* Left: Summary */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "#e3f2fd",
                border: "1px solid #90caf9",
                minWidth: 120,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" color="primary" fontWeight="bold">
                {allApplicationForms.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("total_applications")}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "#fff3e0",
                border: "1px solid #ff9800",
                minWidth: 120,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" color="warning.main" fontWeight="bold">
                {pendingApplications.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("pending")}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "#e8f5e8",
                border: "1px solid #4caf50",
                minWidth: 120,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" color="success.main" fontWeight="bold">
                {approvedApplications.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("approved")}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "#ffebee",
                border: "1px solid #f44336",
                minWidth: 120,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" color="error.main" fontWeight="bold">
                {rejectedApplications.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t("rejected")}
              </Typography>
            </Box>
          </Box>

          {/* Right: Search + Filter */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextField
              size="small"
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />

            <TextField
              fullWidth
              select
              label={t("status")}
              name="status"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              size="small"
              margin="dense"
              sx={{ backgroundColor: '#f8f9fb', minWidth: 120 }}
              required
              disabled={false}
            >
              <MenuItem value="All">{t("all")}</MenuItem>
              <MenuItem value="Pending">{t("pending")}</MenuItem>
              <MenuItem value="Approved">{t("approved")}</MenuItem>
              <MenuItem value="Rejected">{t("rejected")}</MenuItem>
            </TextField>
          </Box>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                  {t("s_no")}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                    <Checkbox
                      checked={
                        (pendingApplications.length + rejectedApplications.length) > 0 &&
                        selectedApplications.length === Math.min(pendingApplications.length + rejectedApplications.length, 150)
                      }
                      indeterminate={
                        (pendingApplications.length + rejectedApplications.length) > 0 &&
                        selectedApplications.length > 0 &&
                        selectedApplications.length < Math.min(pendingApplications.length + rejectedApplications.length, 150)
                      }
                      onChange={handleSelectAll}
                      disabled={(pendingApplications.length + rejectedApplications.length) === 0}
                    />
                    <Typography variant="caption" sx={{ color: "white", fontSize: "0.7rem", textAlign: "center" }}>
                      {(pendingApplications.length + rejectedApplications.length) > 150 ? t("select_150") : t("select_all")}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                  {t("application_no")}
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                  {t("student_name")}
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                  {t("gender")}
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                  {t("status")}
                </TableCell>
                {(isSchoolAdmin() || isTeacher()) && (
                  <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "primary.light", color: "white" }}>
                    {t("actions")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={(isSchoolAdmin() || isTeacher()) ? 7 : 6} align="center">
                    {t("no_data_found")}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow hover key={row.id || index}>
                    <TableCell align="center">{index + 1 + page * rowsPerPage}</TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          selectedApplications.length >= 150 && !selectedApplications.includes(row.id!)
                            ? t("maximum_150_students_selected")
                            : t("click_to_select_application")
                        }
                        placement="top"
                      >
                        <Checkbox
                          checked={row.id !== undefined && selectedApplications.includes(row.id)}
                          onChange={() => {
                            if (row.id !== undefined) handleSelectApplication(row.id);
                          }}
                          disabled={
                            (row.isRegistered?.toLowerCase() !== 'pending' && row.isRegistered?.toLowerCase() !== 'rejected') ||
                            row.id === undefined ||
                            (selectedApplications.length >= 150 && !selectedApplications.includes(row.id))
                          }
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.applicationNo}</TableCell>
                    <TableCell>{row.studentName}</TableCell>
                    <TableCell>{formatGender(row.gender)}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          backgroundColor:
                            row.isRegistered?.toLowerCase() === 'rejected' ? "#ffebee" :
                              row.isRegistered?.toLowerCase() === 'approved' ? "#e8f5e8" :
                                "#fff3e0",
                          color:
                            row.isRegistered?.toLowerCase() === 'rejected' ? "#d32f2f" :
                              row.isRegistered?.toLowerCase() === 'approved' ? "#2e7d32" :
                                "#f57c00",
                          border: `1px solid ${row.isRegistered?.toLowerCase() === 'rejected' ? "#f44336" :
                            row.isRegistered?.toLowerCase() === 'approved' ? "#4caf50" :
                              "#ff9800"
                            }`,
                        }}
                      >
                        {row.isRegistered?.toLowerCase() === 'rejected' ? t("rejected") :
                          row.isRegistered?.toLowerCase() === 'approved' ? t("approved") :
                            t("pending")}
                      </Box>
                    </TableCell>
                    {(isSchoolAdmin() || isTeacher()) && (
                      <TableCell align="center">
                        <IconButton
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setSelectedRow(row);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredApplicationForms.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(+event.target.value);
            setPage(0);
          }}
        />
      </Paper>

      {(isSchoolAdmin() || isTeacher()) && (
        <ApplicationFormModal
          key={currentForm?.id || 'new'} // Force re-render when editing different applications
          open={modalOpen}
          onClose={handleCloseModal}
          initialData={currentForm}
          onSubmit={handleSubmit}
          isEdit={isEdit}
          isStudentEdit={false}
          schoolData={schoolData as any}
          selectedClass={selectedClass}
        />
      )}

      {(isSchoolAdmin() || isTeacher()) && (
        <UploadBulkApplicationsModal open={openBulkModal} handleClose={() => setOpenBulkModal(false)} schoolId={schoolId} />
      )}

      {/* Action Menu */}
      {(isSchoolAdmin() || isTeacher()) && (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          {/* View - Always available for all statuses */}
          <MenuItem
            onClick={() => {
              if (selectedRow) {
                setViewAdmissionData(selectedRow);
                setViewAdmissionModalOpen(true);
              }
              setAnchorEl(null);
            }}
          >
            {t("view")}
          </MenuItem>

          {/* Actions for PENDING applications - Show all actions */}
          {selectedRow && selectedRow.isRegistered?.toLowerCase() === 'pending' && (
            <>
              <MenuItem
                onClick={() => {
                  if (selectedRow) handleOpenEditModal(selectedRow);
                  setAnchorEl(null);
                }}
              >
                {t("edit")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (selectedRow?.id) {
                    setFormToApprove(selectedRow.id);
                    setApproveDialogOpen(true);
                  }
                  setAnchorEl(null);
                }}
                disabled={isApplicationSessionInactive(selectedRow)}
                sx={{
                  color: isApplicationSessionInactive(selectedRow) ? "text.disabled" : "success.main",
                  opacity: isApplicationSessionInactive(selectedRow) ? 0.5 : 1
                }}
              >
                {t("register_as_student")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (selectedRow?.id) {
                    setFormToReject(selectedRow.id);
                    setRejectDialogOpen(true);
                  }
                  setAnchorEl(null);
                }}
                sx={{ color: "warning.main" }}
              >
                {t("reject")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (selectedRow?.id) {
                    setFormToDelete(selectedRow.id);
                    setDeleteDialogOpen(true);
                  }
                  setAnchorEl(null);
                }}
                sx={{ color: "error.main" }}
              >
                {t("delete")}
              </MenuItem>
            </>
          )}

          {/* Actions for REJECTED applications - Show Edit, Register as Student, and Delete */}
          {selectedRow && selectedRow.isRegistered?.toLowerCase() === 'rejected' && (
            <>
              <MenuItem
                onClick={() => {
                  if (selectedRow) handleOpenEditModal(selectedRow);
                  setAnchorEl(null);
                }}
              >
                {t("edit")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (selectedRow?.id) {
                    setFormToApprove(selectedRow.id);
                    setApproveDialogOpen(true);
                  }
                  setAnchorEl(null);
                }}
                disabled={isApplicationSessionInactive(selectedRow)}
                sx={{
                  color: isApplicationSessionInactive(selectedRow) ? "text.disabled" : "success.main",
                  opacity: isApplicationSessionInactive(selectedRow) ? 0.5 : 1
                }}
              >
                {t("register_as_student")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (selectedRow?.id) {
                    setFormToDelete(selectedRow.id);
                    setDeleteDialogOpen(true);
                  }
                  setAnchorEl(null);
                }}
                sx={{ color: "error.main" }}
              >
                {t("delete")}
              </MenuItem>
            </>
          )}

          {/* Actions for APPROVED applications - allow delete only when student's status is false */}
          {selectedRow &&
            selectedRow.isRegistered?.toLowerCase() === "approved" &&
            isApplicationStudentInactive(selectedRow) && (
              <MenuItem
                onClick={() => {
                  if (selectedRow?.id) {
                    setFormToDelete(selectedRow.id);
                    setDeleteDialogOpen(true);
                  }
                  setAnchorEl(null);
                }}
                sx={{ color: "error.main" }}
              >
                {t("delete")}
              </MenuItem>
            )}
        </Menu>
      )}

      {/* Bulk Approve Dialog */}
      {(isSchoolAdmin() || isTeacher()) && (
        <Dialog
          open={bulkApproveDialogOpen}
          onClose={bulkApproveLoading ? undefined : () => setBulkApproveDialogOpen(false)}
          disableEscapeKeyDown={bulkApproveLoading}
        >
          <DialogTitle>{t("confirm_bulk_approval")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("are_you_sure_you_want_to_approve")} {selectedApplications.length} {t("selected_application")}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setBulkApproveDialogOpen(false)}
              disabled={bulkApproveLoading}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                setBulkApproveLoading(true);
                try {
                  await handleBulkApprove(selectedApplications);
                } catch (error) {
                } finally {
                  setBulkApproveLoading(false);
                }
              }}
              disabled={bulkApproveLoading}
              startIcon={bulkApproveLoading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" },
                minWidth: "140px",
                "&:disabled": { opacity: bulkApproveLoading ? 0.8 : 0.6 },
              }}
            >
              {bulkApproveLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    Processing: {bulkApproveProgress.currentStudentName || '...'}
                  </Typography>
                </Box>
              ) : (
                t("approve_all")
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Bulk Reject Dialog */}
      {(isSchoolAdmin() || isTeacher()) && (
        <Dialog open={bulkRejectDialogOpen} onClose={() => { }} disableEscapeKeyDown>
          <DialogTitle>{t("confirm_bulk_rejection")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("are_you_sure_you_want_to_reject")} {selectedApplications.length} {t("selected_application")}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setBulkRejectDialogOpen(false)}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                setBulkRejectLoading(true);
                try {
                  for (const applicationId of selectedApplications) {
                    await rejectApplicationFormMutation.mutateAsync(applicationId);
                    // Optimistically update cache to set isRegistered: 'Rejected'
                    updateAppInCaches(applicationId, (f) => ({ ...f, isRegistered: 'Rejected' }));
                  }
                  showSuccess(t("applications_rejected_successfully", { count: selectedApplications.length }));
                  setSelectedApplications([]);
                  setBulkRejectDialogOpen(false);

                  // OPTIONAL: light background refresh of inactive only
                  queryClient.refetchQueries({
                    queryKey: ["applicationForms"],
                    type: "inactive",
                  });
                  queryClient.refetchQueries({
                    queryKey: ["applicationFormsBySchoolId", schoolId],
                    type: "inactive",
                  });

                  if (selectedStatus !== "All") setPage(0);
                } catch (error: any) {
                  const errorMessage = error?.response?.data?.message || error?.message || t("error_rejecting_applications");
                  showError(errorMessage);
                } finally {
                  setBulkRejectLoading(false);
                }
              }}
              disabled={bulkRejectLoading}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {bulkRejectLoading ? <CircularProgress size={20} /> : t("reject_all")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Dialog */}
      {(isSchoolAdmin() || isTeacher()) && (
        <Dialog open={deleteDialogOpen} onClose={() => { }} disableEscapeKeyDown>
          <DialogTitle>{t("confirm_deletion")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("are_you_sure_you_want_to_delete_this_application_form")}
              <br />
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setFormToDelete(null);
              }}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                if (formToDelete !== null) {
                  try {
                    // Call delete API
                    await deleteApplicationFormMutation.mutateAsync(formToDelete);
                    // Optimistically remove from cache
                    removeAppFromCaches(formToDelete);

                    // Invalidate sessionId/classId query when deleting to show updated list immediately
                    if (selectedSession?.id && selectedClass?.id) {
                      queryClient.invalidateQueries({
                        queryKey: ["applicationFormsBySessionIdAndClassId", selectedSession.id, selectedClass.id]
                      });
                    }

                    showSuccess(t("application_form_deleted_successfully"));
                    setDeleteDialogOpen(false);
                    setFormToDelete(null);
                    if (selectedStatus !== "All") setPage(0);
                  } catch (error: any) {
                    const errorMessage = error?.response?.data?.message || error?.message || t("error_deleting_application");
                    showError(errorMessage);
                  }
                }
              }}
              sx={{
                backgroundColor: "#d32f2f",
                color: "white",
                "&:hover": { backgroundColor: "#b71c1c" }
              }}
            >
              {t("delete")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Reject Dialog */}
      {(isSchoolAdmin() || isTeacher()) && (
        <Dialog open={rejectDialogOpen} onClose={() => { }} disableEscapeKeyDown>
          <DialogTitle>{t("confirm_rejection")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("are_you_sure_you_want_to_reject_this_application")}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setRejectDialogOpen(false)}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={() => {
                if (formToReject !== null) handleRejectApplication(formToReject);
              }}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {t("reject")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Approve Dialog */}
      {(isSchoolAdmin() || isTeacher()) && (
        <Dialog open={approveDialogOpen} onClose={() => { }} disableEscapeKeyDown>
          <DialogTitle>{t("confirm_approve")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("are_you_sure_want_to_register_this_student")}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setApproveDialogOpen(false)}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={() => {
                if (formToApprove !== null) handleApproveApplicationWithConfirmation(formToApprove);
              }}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {t("approve")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* View Admission Modal */}
      {(isSchoolAdmin() || isTeacher()) && (
        <Dialog open={viewAdmissionModalOpen} onClose={() => { }} maxWidth="md" fullWidth disableEscapeKeyDown>
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{t("admission_form_preview")}</span>
              <IconButton onClick={() => {
                // Invalidate photo queries when closing to ensure fresh data on next open
                if (viewAdmissionData?.id) {
                  queryClient.invalidateQueries({
                    queryKey: ["applicationPhotoInfo", viewAdmissionData.id],
                  });
                  queryClient.invalidateQueries({
                    queryKey: ["applicationPhoto"],
                  });
                }
                setViewAdmissionModalOpen(false);
              }} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {viewAdmissionData && (
              <>
                <ApplicationFormTemplate
                  key={`${viewAdmissionData.id}-${viewAdmissionModalOpen}`} // Force re-render when modal opens/closes
                  data={viewAdmissionData}
                  schoolData={schoolData}
                  renderForPDF={false}
                />
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Download Modal */}
      {(isSchoolAdmin() || isTeacher()) && (
        <Dialog
          open={bulkDownloadModalOpen}
          onClose={isGeneratingBulk ? undefined : handleCloseBulkDownloadModal}
          disableEscapeKeyDown={isGeneratingBulk}
          keepMounted
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="subtitle1" fontWeight="bold">{t("bulk_download_application_forms")}</Typography>
          </DialogTitle>

          <DialogContent sx={{ position: 'relative' }}>
            {isGeneratingBulk && (
              <Box sx={{
                position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.9)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
              }}>
                <Typography variant="h6" gutterBottom>{t("generating_pdf")}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("processing_application", { applicationName: currentApplication })}
                </Typography>
                <Box sx={{ width: '100%', mb: 1 }}><LinearProgress variant="determinate" value={bulkDownloadProgress} /></Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {bulkDownloadProgress}% {t("complete")}
                </Typography>
                <Button variant="outlined" color="error" onClick={handleCancelBulkDownload} sx={{ mt: 2 }} size="small">
                  {t("cancel_download")}
                </Button>
              </Box>
            )}

            {bulkDownloadError && <Alert severity="error" sx={{ mb: 2 }}>{bulkDownloadError}</Alert>}
            {bulkDownloadSuccess && <Alert severity="success" sx={{ mb: 2 }}>{bulkDownloadSuccess}</Alert>}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t("session")}</InputLabel>
                <Select value={bulkDownloadSelectedSession} onChange={handleBulkDownloadSessionChange} label={t("session")} disabled={isGeneratingBulk}>
                  {sessionsQuery?.data?.map((session: any) => (
                    <MenuItem key={session.id} value={session.id}>{session.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t("class")}</InputLabel>
                <Select value={bulkDownloadSelectedClass} onChange={handleBulkDownloadClassChange} label={t("class")} disabled={isGeneratingBulk || !bulkDownloadSelectedSession}>
                  {classesQuery?.data?.map((cls: any) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {cls.section ? `${cls.name} ${cls.section}` : cls.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {bulkDownloadSelectedClass &&
                bulkDownloadSelectedSession &&
                bulkDownloadVisibleApplications.length > 0 && (
                  <TableContainer sx={{ maxHeight: 280, mt: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ backgroundColor: "background.paper" }}>
                            <Checkbox
                              checked={bulkDownloadSelectAllChecked}
                              indeterminate={bulkDownloadSelectAllIndeterminate}
                              disabled={isGeneratingBulk}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setBulkDownloadSelectedFormIds(
                                    new Set(
                                      bulkDownloadVisibleApplications.map((f: any) => f.id as number)
                                    )
                                  );
                                } else {
                                  setBulkDownloadSelectedFormIds(new Set());
                                }
                              }}
                              inputProps={{
                                "aria-label": (t("select_all") as string) || "Select All",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", backgroundColor: "background.paper" }}>
                            {(t("select_all") as string) || "Select All"} / {t("student_name")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkDownloadVisibleApplications.map((form: any) => (
                          <TableRow key={form.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={bulkDownloadSelectedFormIds.has(form.id)}
                                disabled={isGeneratingBulk}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setBulkDownloadSelectedFormIds((prev) => {
                                    const next = new Set(prev);
                                    if (checked) next.add(form.id);
                                    else next.delete(form.id);
                                    return next;
                                  });
                                }}
                              />
                            </TableCell>
                            <TableCell>{form.studentName || t("unknown_student")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCloseBulkDownloadModal();
              }}
              disabled={isGeneratingBulk}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleBulkDownload();
              }}
              disabled={
                isGeneratingBulk ||
                !bulkDownloadSelectedClass ||
                !bulkDownloadSelectedSession ||
                bulkDownloadSelectedFormIds.size === 0
              }
              variant="contained"
              sx={{
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" },
              }}
            >
              {isGeneratingBulk ? t("generating") : t("download_pdf")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Restore Draft Dialog */}
      <Dialog
        open={restoreDraftDialogOpen}
        onClose={handleDiscardDraft}
        aria-labelledby="restore-draft-dialog-title"
        aria-describedby="restore-draft-dialog-description"
      >
        <DialogTitle id="restore-draft-dialog-title">
          {t('restore_previous_data') || 'Restore Previous Data?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="restore-draft-dialog-description">
            {t('restore_draft_message') || 'We found previously filled form data. Would you like to restore it?'}
            <br />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('restore_draft_note') || 'Note: Uploaded images will need to be re-uploaded.'}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDiscardDraft}
            variant="outlined"
            sx={{ borderColor: "#666", color: "#666" }}
          >
            {t('no_start_fresh') || 'No, Start Fresh'}
          </Button>
          <Button
            onClick={handleRestoreDraft}
            variant="contained"
            autoFocus
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {t('yes_restore') || 'Yes, Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
